import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { ChevronRight, ChevronDown, FlaskConical, Trash2, Loader2, MailCheck, MailWarning, MailPlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ADMIN } from "@/constants/testIds";
import EmailProviderPanel from "@/components/admin/EmailProviderPanel";
import EmailSendLogPanel from "@/components/admin/EmailSendLogPanel";

// Client-side heuristic that mirrors the backend `is_test_user` rule so the
// UI groups users the same way even before the server has computed the flag.
// The persisted `is_test` boolean (set via the toggle) takes precedence.
function isTestUser(u) {
  if (u?.is_test === true) return true;
  const e = (u?.email || "").toLowerCase();
  if (!e || !e.includes("@")) return false;
  const [local, domain] = e.split("@");
  const testDomains = ["example.com", "example.org", "example.net", "test.com", "t.com", "ex.com"];
  if (testDomains.includes(domain)) return true;
  if (domain.endsWith(".test") || domain.endsWith(".local")) return true;
  if (/^test[._-]/.test(local)) return true;
  if (local.includes("+test")) return true;
  return false;
}

function UserRow({ u, me, setRole, setTestFlag, onManualVerify, onResendVerify }) {
  const isSelf = u.user_id === me.user_id;
  // Only email/password sign-ups need verification. Google OAuth accounts
  // arrive already-verified so we don't show the button for them.
  const usesPassword = Array.isArray(u.auth_methods) && u.auth_methods.includes("password");
  const isVerified = u.email_verified === true;
  const canManualVerify = usesPassword && !isVerified;
  return (
    <li className="py-3 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full"/> : <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700"/>}
        <div>
          <div className="font-medium text-slate-900 dark:text-slate-100 flex items-center gap-2">
            {u.name}
            {usesPassword && (
              isVerified ? (
                <span
                  data-testid={`user-verified-badge-${u.user_id}`}
                  title={u.manually_verified_at ? `Manually verified ${new Date(u.manually_verified_at).toLocaleString()}` : "Email verified"}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400"
                >
                  <MailCheck className="w-3 h-3"/>
                  {u.manually_verified_at ? "Verified (manual)" : "Verified"}
                </span>
              ) : (
                <span
                  data-testid={`user-unverified-badge-${u.user_id}`}
                  title="Signed up with email/password but never confirmed"
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-500"
                >
                  <MailWarning className="w-3 h-3"/>
                  Unverified
                </span>
              )
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{u.email}</div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {canManualVerify && (
          <>
            <Button
              data-testid={`user-resend-verify-btn-${u.user_id}`}
              onClick={() => onResendVerify(u)}
              variant="outline"
              size="sm"
              className="border-sky-300 text-sky-700 hover:bg-sky-50 dark:border-sky-800 dark:text-sky-400 dark:hover:bg-sky-950/30"
            >
              <MailPlus className="w-3.5 h-3.5 mr-1.5"/>
              Resend email
            </Button>
            <Button
              data-testid={`user-manual-verify-btn-${u.user_id}`}
              onClick={() => onManualVerify(u)}
              variant="outline"
              size="sm"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30"
            >
              <MailCheck className="w-3.5 h-3.5 mr-1.5"/>
              Manual confirm
            </Button>
          </>
        )}
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 select-none"
               title={isSelf ? "You can't flag yourself" : "Mark this account as a test user (will be swept by the Remove button)"}>
          <span>Test</span>
          <Switch
            data-testid={`user-test-flag-${u.user_id}`}
            checked={!!u.is_test}
            disabled={isSelf}
            onCheckedChange={(v) => setTestFlag(u, v)}
            className="data-[state=checked]:bg-amber-600"
          />
        </label>
        <Select value={u.role} onValueChange={(v)=>setRole(u, v)} disabled={isSelf}>
          <SelectTrigger data-testid={ADMIN.userRoleSelect(u.user_id)} className="w-32"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </li>
  );
}

export default function UsersPanel({ me, users, reload }) {
  const [showTest, setShowTest] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null); // {count, users}
  const [busy, setBusy] = useState(false);
  // Manual-verify state — populated when admin clicks a "Manual confirm"
  // button on an unverified user row.
  const [verifyTarget, setVerifyTarget] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  // Resend-verification state — mirrors verify state but keeps its own
  // dialog + busy flag so an admin can queue both actions independently.
  const [resendTarget, setResendTarget] = useState(null);
  const [resendBusy, setResendBusy] = useState(false);
  const [resendResult, setResendResult] = useState(null); // {sent_at, dev_verification_url?}
  // Confirm target for turning the Test-user slider ON. Unmarking bypasses
  // the confirm since it's a safe restore.
  const [testFlagTarget, setTestFlagTarget] = useState(null);
  const [testFlagBusy, setTestFlagBusy] = useState(false);

  const setRole = async (u, role) => {
    const prev = u.role;
    if (role === prev) return;
    try {
      await api.put(`/users/${u.user_id}/role`, { role });
      toast.success(`${u.email} → ${role}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/users/${u.user_id}/role`, { role: prev });
              toast.success(`${u.email} restored to ${prev}`);
              reload();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't undo role change");
            }
          },
        },
      });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change role");
    }
  };

  const setTestFlag = async (u, is_test) => {
    // Marking as test is destructive — that user will be included in the
    // Remove Test Users sweep and their sessions/bookmarks/comments deleted.
    // Prompt for confirmation. Unmarking is safe → apply immediately, but
    // surface a 5-second Undo affordance so an accidental restore can be
    // reversed with one click.
    if (is_test) {
      setTestFlagTarget(u);
      return;
    }
    try {
      await api.patch(`/users/${u.user_id}/test-flag`, { is_test: false });
      toast.success(`${u.email} unmarked as test user`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.patch(`/users/${u.user_id}/test-flag`, { is_test: true });
              toast.success(`${u.email} re-marked as test user`);
              reload();
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Couldn't undo — try the slider again");
            }
          },
        },
      });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update test flag");
    }
  };

  const doMarkAsTest = async () => {
    if (!testFlagTarget) return;
    setTestFlagBusy(true);
    try {
      await api.patch(`/users/${testFlagTarget.user_id}/test-flag`, { is_test: true });
      toast.success(`${testFlagTarget.email} marked as test user`);
      setTestFlagTarget(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update test flag");
    } finally {
      setTestFlagBusy(false);
    }
  };

  const openConfirm = async () => {
    try {
      const r = await api.get("/admin/test-users");
      setPreview(r.data);
      setConfirmOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load test-user preview");
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/admin/test-users");
      const n = r.data?.deleted_users || 0;
      toast.success(n ? `Removed ${n} test user${n === 1 ? "" : "s"} and cascaded data.` : "No test users matched.");
      setConfirmOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove test users");
    } finally {
      setBusy(false);
    }
  };

  const doManualVerify = async () => {
    if (!verifyTarget) return;
    setVerifyBusy(true);
    try {
      await api.post(`/users/${verifyTarget.user_id}/manual-verify`);
      toast.success(`${verifyTarget.email} confirmed manually.`);
      setVerifyTarget(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't manually confirm user");
    } finally {
      setVerifyBusy(false);
    }
  };

  const doResendVerify = async () => {
    if (!resendTarget) return;
    setResendBusy(true);
    setResendResult(null);
    try {
      const r = await api.post(`/users/${resendTarget.user_id}/resend-verification`);
      setResendResult(r.data);
      // In prod (Resend configured) the endpoint won't include the dev URL,
      // so we close the dialog and show a success toast. In dev/preview we
      // keep it open so the admin can copy the link.
      if (!r.data?.dev_verification_url) {
        toast.success(`Verification email sent to ${resendTarget.email}.`);
        setResendTarget(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't resend verification email");
    } finally {
      setResendBusy(false);
    }
  };

  const realUsers = users.filter(u => !isTestUser(u));
  const testUsers = users.filter(u => isTestUser(u));

  return (
    <Card className="border-slate-200 dark:border-slate-800 mt-6">
      <CardHeader><CardTitle className="font-heading">Users</CardTitle></CardHeader>
      <CardContent>
        <EmailProviderPanel/>
        <EmailSendLogPanel/>
        {users.length === 0 ? (
          <div className="py-6 text-slate-400 dark:text-slate-500 text-center">Loading users…</div>
        ) : (
          <>
            <ul className="divide-y divide-slate-200 dark:divide-slate-800 border-y border-slate-200 dark:border-slate-800 text-sm">
              {realUsers.map(u => (
                <UserRow key={u.user_id} u={u} me={me} setRole={setRole} setTestFlag={setTestFlag} onManualVerify={setVerifyTarget} onResendVerify={setResendTarget}/>
              ))}
              {realUsers.length === 0 && (
                <li className="py-6 text-slate-400 dark:text-slate-500 text-center italic">
                  No non-test users yet.
                </li>
              )}
            </ul>

            {testUsers.length > 0 && (
              <div className="mt-6 border border-slate-200 dark:border-slate-800 rounded-lg overflow-hidden">
                <div className="flex items-stretch">
                  <button
                    data-testid="users-test-toggle"
                    onClick={() => setShowTest(v => !v)}
                    className="flex-1 flex items-center justify-between gap-2 px-4 py-3 text-sm font-heading font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                  >
                    <span className="inline-flex items-center gap-2">
                      {showTest
                        ? <ChevronDown className="w-4 h-4 text-slate-400 dark:text-slate-500"/>
                        : <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500"/>}
                      <FlaskConical className="w-4 h-4 text-slate-400 dark:text-slate-500"/>
                      Test users
                      <span className="ml-1 text-xs uppercase tracking-widest text-slate-400 dark:text-slate-500 font-normal">
                        ({testUsers.length})
                      </span>
                    </span>
                    <span className="text-xs uppercase tracking-widest text-slate-400 dark:text-slate-500 font-normal">
                      {showTest ? "Hide" : "Show"}
                    </span>
                  </button>
                  <div className="border-l border-slate-200 dark:border-slate-800 flex items-center px-3">
                    <Button
                      data-testid="remove-test-users-btn"
                      onClick={openConfirm}
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/30"
                    >
                      <Trash2 className="w-4 h-4 mr-2"/>
                      Remove {testUsers.length} test user{testUsers.length === 1 ? "" : "s"}
                    </Button>
                  </div>
                </div>
                {showTest && (
                  <ul data-testid="users-test-list" className="divide-y divide-slate-200 dark:divide-slate-800 border-t border-slate-200 dark:border-slate-800 text-sm">
                    {testUsers.map(u => (
                      <UserRow key={u.user_id} u={u} me={me} setRole={setRole} setTestFlag={setTestFlag} onManualVerify={setVerifyTarget} onResendVerify={setResendTarget}/>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="remove-test-users-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remove test users?</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              This deletes {preview?.count ?? 0} account{preview?.count === 1 ? "" : "s"} plus every
              session, bookmark, comment, feedback item, and analytics event they generated. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {preview?.users?.length > 0 && (
            <div className="max-h-48 overflow-y-auto text-sm border border-slate-200 dark:border-slate-800 rounded-md bg-slate-50 dark:bg-slate-900 p-2"
                 data-testid="remove-test-users-preview-list">
              <ul className="space-y-1">
                {preview.users.map(u => (
                  <li key={u.user_id} className="flex items-center justify-between gap-2 text-slate-700 dark:text-slate-300">
                    <span className="truncate">{u.email}</span>
                    <span className="text-xs text-slate-400 dark:text-slate-500 uppercase tracking-widest">{u.role}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={busy}
                    className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="remove-test-users-confirm"
              onClick={doDelete}
              disabled={busy || (preview?.count ?? 0) === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              Yes, remove {preview?.count ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual email confirmation — used when a user never received (or
          couldn't retrieve) their verification email. */}
      <Dialog open={!!verifyTarget} onOpenChange={(o) => !o && setVerifyTarget(null)}>
        <DialogContent data-testid="manual-verify-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <MailCheck className="w-5 h-5 text-emerald-600 dark:text-emerald-400"/>
              Manually confirm this account?
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Mark <span className="font-mono text-slate-900 dark:text-slate-100">{verifyTarget?.email}</span> as
              email-verified. Use this when a user never received their confirmation email
              (Yahoo/AOL blocks, spam-folder loss, typo'd address, etc.) and you've verified
              their identity another way.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed space-y-1 border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-50 dark:bg-slate-900">
            <div>• Their pending verification link will be invalidated.</div>
            <div>• The action is logged: <span className="font-mono">manually_verified_by</span> = you, <span className="font-mono">manually_verified_at</span> = now.</div>
            <div>• They'll be able to sign in immediately with their existing password.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyTarget(null)} disabled={verifyBusy}
                    className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="manual-verify-confirm"
              onClick={doManualVerify}
              disabled={verifyBusy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {verifyBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              Yes, confirm this user
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resend verification email — sends a fresh confirmation link and
          invalidates any prior pending tokens for this user. */}
      <Dialog open={!!resendTarget} onOpenChange={(o) => {
        if (!o) { setResendTarget(null); setResendResult(null); }
      }}>
        <DialogContent data-testid="resend-verify-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <MailPlus className="w-5 h-5 text-sky-600 dark:text-sky-400"/>
              Resend verification email?
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Send a fresh confirmation link to <span className="font-mono text-slate-900 dark:text-slate-100">{resendTarget?.email}</span>.
              Their previous link (if any) will be invalidated so only the newest email works.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-50 dark:bg-slate-900 space-y-1">
            <div>• The user must click the link within <span className="font-mono">24 hours</span>.</div>
            <div>• Ask them to check Spam / Trash if it doesn't appear in the inbox.</div>
            <div>• If it still doesn't arrive, use <em>Manual confirm</em> instead.</div>
          </div>
          {resendResult?.dev_verification_url && (
            <div
              data-testid="resend-verify-dev-link"
              className="mt-2 text-xs border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 rounded-md p-3 space-y-2"
            >
              <div className="uppercase tracking-widest text-amber-700 dark:text-amber-500 font-bold">Dev / preview — no Resend key configured</div>
              <div className="text-slate-700 dark:text-slate-300 leading-relaxed">Forward this link to the user directly:</div>
              <input
                readOnly
                value={resendResult.dev_verification_url}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full font-mono text-[11px] px-2 py-1.5 border border-amber-300 dark:border-amber-800 rounded bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              />
              <button
                onClick={() => navigator.clipboard.writeText(resendResult.dev_verification_url).then(() => toast.success("Copied verification link"))}
                className="text-amber-700 dark:text-amber-400 hover:underline font-semibold"
              >
                Copy to clipboard
              </button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResendTarget(null); setResendResult(null); }} disabled={resendBusy}
                    className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              {resendResult ? "Close" : "Cancel"}
            </Button>
            {!resendResult && (
              <Button
                data-testid="resend-verify-confirm"
                onClick={doResendVerify}
                disabled={resendBusy}
                className="bg-sky-600 hover:bg-sky-700 text-white"
              >
                {resendBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
                Yes, send email
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm before turning the Test-user slider ON — that account will
          be swept by the "Remove test users" bulk delete along with its
          sessions, bookmarks, comments, and analytics events. */}
      <Dialog open={!!testFlagTarget} onOpenChange={(o) => !o && setTestFlagTarget(null)}>
        <DialogContent data-testid="mark-test-user-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="inline-flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-amber-600 dark:text-amber-400"/>
              Mark as test user?
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              <span className="font-mono text-slate-900 dark:text-slate-100">{testFlagTarget?.email}</span> will
              be classified as a test account. This is a flag change — it does not delete anything now, but the
              account will be swept up whenever you click <em>Remove test users</em>.
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-50 dark:bg-slate-900 space-y-1">
            <div>• Their sessions, bookmarks, comments, and analytics events will delete on the next sweep.</div>
            <div>• Test users are also excluded from analytics counts.</div>
            <div>• Flip the slider off any time to restore normal-user status — no data is touched until you press <em>Remove</em>.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestFlagTarget(null)} disabled={testFlagBusy}
                    className="border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300">
              Cancel
            </Button>
            <Button
              data-testid="mark-test-user-confirm"
              onClick={doMarkAsTest}
              disabled={testFlagBusy}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {testFlagBusy && <Loader2 className="w-4 h-4 mr-2 animate-spin"/>}
              Yes, mark as test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
